import { createSelector } from 'reselect';

import variables from '../../_variables.scss';

import { PAGE_KEY_MAP } from '../../constants/experience';
import { FLOW_ROUTES, PICK_YOUR_GIFT_STEP_LABEL, ROUTE_NAMES } from '../../constants/routes';
import { DEFAULT_LOGO_URL, LOGO_SCALE_MAX, LOGO_SCALE_MIN } from '../../constants/ui';
import { ExperiencePageKeyEnum, IExperience, IExperiencePageOverride } from '../../types/api';
import { ISuspendedSelection } from '../../types/country';
import { ICustomizableViewItem } from '../../types/inventory';
import { ICampaign, ICampaignItem } from '../../types/pyg';
import { BackgroundTypeEnum } from '../../types/shell';
import { IAppState } from '../../types/store';
import { getPickedItemsByDefault, getUniqueItemsWithCounts, isMskuItem, nullifyEmpty } from '../../util/helpers';
import { getPickedMarketplaceItemName, isMarketplaceId, mapCartLinesToCampaignItems } from '../../util/marketplace';
import {
  selectCustomizableItems,
  selectIsKitPicked,
  selectPickedCampaignId,
  selectPickedItems,
  selectSuspendedPick,
} from './form';
import {
  selectBaseColor,
  selectCampaigns,
  selectExperience,
  selectInitialAddress,
  selectIsCountryGated,
  selectIsPYGFlow,
  selectItems,
  selectOrgData,
  selectWhiteLabeling,
} from './init';
import {
  selectHasGiftBudget,
  selectHasMarketplaceItems,
  selectIsMarketplaceItemOutOfStockFn,
  selectMarketplaceCart,
  selectMarketplaceDescriptionFn,
  selectSuspendedCart,
} from './marketplace';

const getPageKey = (currentRoute: string): ExperiencePageKeyEnum | undefined =>
  PAGE_KEY_MAP[currentRoute] as ExperiencePageKeyEnum | undefined;

const getExperienceOverride = <T>(
  experience: IExperience | null,
  currentRoute: string,
  getter: (override: IExperiencePageOverride) => T | undefined,
): T | undefined => {
  const pageKey = getPageKey(currentRoute);
  if (!pageKey) {
    return undefined;
  }
  const overrides = experience?.pages?.[pageKey]?.overrides;
  if (!overrides) {
    return undefined;
  }

  return nullifyEmpty(getter(overrides));
};

export const selectPickedCampaign = createSelector(selectPickedCampaignId, selectCampaigns, (campaignId, campaigns) =>
  campaignId && campaigns?.length ? campaigns.find(({ id }: ICampaign) => id === campaignId) : undefined,
);

// The lone-campaign fallback names a send whose single campaign was auto-picked. A budget claim
// must not borrow it: the recipient chose marketplace items instead of that campaign, so its
// name belongs to nothing they selected.
export const selectSelectionCampaignName = createSelector(
  selectPickedCampaign,
  selectCampaigns,
  selectMarketplaceCart,
  (picked, campaigns, cart) => {
    if (picked?.name) {
      return picked.name;
    }

    const hasLoneCampaign = campaigns?.length === 1 && !cart.length;

    return hasLoneCampaign ? campaigns[0].name : undefined;
  },
);

// Whether the recipient reached the catalog from a page they can return to: any campaign or a
// custom kit means PYG rendered first, while a budget-only send lands on the catalog directly.
export const selectHasPicksToReturnTo = createSelector(
  selectCampaigns,
  selectItems,
  (campaigns, items) => !!campaigns?.length || !!items?.length,
);

// The gift budget is the whole offer: init lands the recipient on the catalog and it stands in
// for the pick step.
export const selectIsBudgetOnlySend = createSelector(
  selectHasGiftBudget,
  selectHasPicksToReturnTo,
  (hasGiftBudget, hasPicks) => hasGiftBudget && !hasPicks,
);

// Custom send + budget: the kit renders as one option next to the catalog entry.
export const selectIsKitChoiceSend = createSelector(
  selectHasGiftBudget,
  selectIsCountryGated,
  selectCampaigns,
  selectItems,
  (hasGiftBudget, isCountryGated, campaigns, items) => {
    if (campaigns?.length || !items?.length) {
      return false;
    }

    const isAllMarketplace = items.every((item) => isMarketplaceId(item.id));

    return hasGiftBudget || (isCountryGated && !isAllMarketplace);
  },
);

// Every item the send offers is a marketplace product, either directly or as the one campaign.
// Deliberately independent of sku_options: routing must not wait on the details fetch.
export const selectIsMarketplaceOnlySend = createSelector(selectCampaigns, selectItems, (campaigns, items): boolean => {
  if (!campaigns?.length) {
    return !!items?.length && items.every((item) => isMarketplaceId(item.id));
  }

  const [campaign] = campaigns;
  const isLoneMarketplaceCampaign = campaigns.length === 1 && !items?.length && isMarketplaceId(campaign.id);

  return (
    isLoneMarketplaceCampaign && !!campaign.items?.length && campaign.items.every((item) => isMarketplaceId(item.id))
  );
});

export const selectSuspendedSelection = createSelector(
  selectSuspendedPick,
  selectSuspendedCart,
  (pick, cart): ISuspendedSelection => ({ pick, cart }),
);

export const selectHasRecipientSelection = createSelector(
  selectPickedCampaignId,
  selectIsKitPicked,
  selectMarketplaceCart,
  (pickedCampaignId, isKitPicked, cart) => !!pickedCampaignId || isKitPicked || cart.length > 0,
);

export const selectHasMarketplaceContent = createSelector(
  selectCampaigns,
  selectHasMarketplaceItems,
  selectHasGiftBudget,
  (campaigns, hasMarketplaceItems, hasGiftBudget) =>
    hasGiftBudget || hasMarketplaceItems || !!campaigns?.some((campaign) => isMarketplaceId(campaign.id)),
);

export const selectHasMSKUItems = createSelector(
  selectItems,
  selectPickedCampaign,
  (items, campaign) => items?.some(isMskuItem) || campaign?.items?.some(isMskuItem),
);

export const selectOrgLogo = (pathname: string) =>
  createSelector(
    selectExperience,
    selectOrgData,
    selectWhiteLabeling,
    selectPickedCampaign,
    (experience, orgInfo, whiteLabeling, pickedCampaign) => {
      const currentRoute = `/${pathname.split('/').at(-1)}`;
      const logoUrl: string | undefined =
        getExperienceOverride(experience, currentRoute, (o) => o.header?.app_bar?.logo_image_url) ??
        nullifyEmpty(experience?.header?.app_bar?.logo_image_url);

      if (logoUrl) {
        return logoUrl;
      }

      return pickedCampaign?.ds_logo_url || whiteLabeling?.ds_logo_url || orgInfo?.logo_url || DEFAULT_LOGO_URL;
    },
  );

export const selectFlowSteps = (path: string) =>
  createSelector(
    selectIsPYGFlow,
    selectHasMSKUItems,
    selectHasMarketplaceItems,
    selectPickedItems,
    selectPickedCampaignId,
    selectHasCustomizableItems,
    selectHasRequiredNonCustomizedItems,
    selectIsBudgetOnlySend,
    selectIsKitChoiceSend,
    (
      isPYGFlow,
      hasMSKU,
      hasMarketplaceItems,
      pickedItems,
      pickedCampaignId,
      hasCustomizableItems,
      hasRequiredNonCustomizedItems,
      isBudgetOnlySend,
      isKitChoiceSend,
    ) =>
      FLOW_ROUTES.includes(path)
        ? [
            // A budget-only send has no picks of its own, so the catalog stands in for PYG.
            ...(isBudgetOnlySend
              ? [
                  {
                    id: ROUTE_NAMES.marketplace,
                    route: ROUTE_NAMES.marketplace,
                    label: PICK_YOUR_GIFT_STEP_LABEL,
                  },
                ]
              : []),
            ...(isPYGFlow || isKitChoiceSend
              ? [
                  {
                    id: ROUTE_NAMES.pyg,
                    route: ROUTE_NAMES.pyg,
                    label: PICK_YOUR_GIFT_STEP_LABEL,
                  },
                ]
              : []),
            ...(hasMSKU || hasMarketplaceItems
              ? [
                  {
                    id: ROUTE_NAMES.pickMsku,
                    route: ROUTE_NAMES.pickMsku,
                    label: 'Pick your item',
                  },
                ]
              : []),
            ...(hasCustomizableItems
              ? [
                  {
                    id: ROUTE_NAMES.customizeItems,
                    route: ROUTE_NAMES.customizeItems,
                    label: 'Customize items',
                  },
                ]
              : []),
            {
              id: ROUTE_NAMES.address,
              route: ROUTE_NAMES.address,
              label: 'Confirm delivery address',
              isDisabled:
                (path.includes(ROUTE_NAMES.pyg) && !pickedCampaignId) ||
                (path.includes(ROUTE_NAMES.pickMsku) && !Object.values(pickedItems).length) ||
                (path.includes(ROUTE_NAMES.customizeItems) && hasRequiredNonCustomizedItems),
            },
          ]
        : [],
  );

export const selectIsFirstStep = (pathname: string) =>
  createSelector(selectFlowSteps(pathname), ([firstStep]) => {
    return firstStep?.route === pathname;
  });

const selectPrioritizedWL = createSelector(
  selectExperience,
  selectPickedCampaign,
  selectWhiteLabeling,
  (experience, pickedCampaign, whiteLabeling) => {
    const {
      ds_shipping_page_title: pickedCampaignSPTitle,
      ds_shipping_page_body: pickedCampaignSPBody,
      ds_msku_page_title: pickedCampaignMSKUTitle,
      ds_msku_page_body: pickedCampaignMSKUBody,
      ds_customize_items_page_title: pickedCampaignCustomizationTitle,
      ds_customize_items_page_body: pickedCampaignCustomizationBody,
    } = pickedCampaign || {};

    const {
      shipping_page_title: orgShippingTitle,
      shipping_page_body: orgShippingBody,
      pick_your_gift_title,
      pick_your_gift_body,
      msku_page_title: orgMKSUTitle,
      msku_page_body: orgMKSUBody,
      customize_items_page_title: orgCustomizeItemsTitle,
      customize_items_page_body: orgCustomizeItemsBody,
      ds_shipping_page_title: campaignShippingTitle,
      ds_shipping_page_body: campaignShippingBody,
      ds_msku_page_title: campaignMKSUTitle,
      ds_msku_page_body: campaignMKSUBody,
      ds_customize_items_page_title: campaignCustomizeItemsTitle,
      ds_customize_items_page_body: campaignCustomizeItemsBody,
    } = whiteLabeling || {};

    const experiencePages = experience?.pages;
    const expShippingHeader = experiencePages?.[ExperiencePageKeyEnum.ShippingPage]?.header_text;
    const expShippingBody = experiencePages?.[ExperiencePageKeyEnum.ShippingPage]?.body_text;
    const expPYGHeader = experiencePages?.[ExperiencePageKeyEnum.PickYourGift]?.header_text;
    const expPYGBody = experiencePages?.[ExperiencePageKeyEnum.PickYourGift]?.body_text;
    const expMskuHeader = experiencePages?.[ExperiencePageKeyEnum.PickYourOption]?.header_text;
    const expMskuBody = experiencePages?.[ExperiencePageKeyEnum.PickYourOption]?.body_text;

    return {
      shipping_page_title: expShippingHeader || pickedCampaignSPTitle || campaignShippingTitle || orgShippingTitle,
      shipping_page_body: expShippingBody || pickedCampaignSPBody || campaignShippingBody || orgShippingBody,
      pick_your_gift_title: expPYGHeader || pick_your_gift_title,
      pick_your_gift_body: expPYGBody || pick_your_gift_body,
      msku_page_title: expMskuHeader || pickedCampaignMSKUTitle || campaignMKSUTitle || orgMKSUTitle,
      msku_page_body: expMskuBody || pickedCampaignMSKUBody || campaignMKSUBody || orgMKSUBody,
      customize_items_page_title:
        pickedCampaignCustomizationTitle || campaignCustomizeItemsTitle || orgCustomizeItemsTitle,
      customize_items_page_body: pickedCampaignCustomizationBody || campaignCustomizeItemsBody || orgCustomizeItemsBody,
    };
  },
);

export const selectGreetingTitle = (pathname: string) =>
  createSelector(
    selectExperience,
    selectInitialAddress,
    selectPrioritizedWL,
    selectIsFirstStep(pathname),
    (experience, receiverData, whiteLabeling, isFirstStep) => {
      const { receiver_first_name } = receiverData || {};
      const { shipping_page_title, pick_your_gift_title, msku_page_title, customize_items_page_title } =
        whiteLabeling || {};

      const currentRoute = `/${pathname.split('/').at(-1)}`;

      const pageKey = getPageKey(currentRoute);
      const pageHeader = pageKey ? nullifyEmpty(experience?.pages?.[pageKey]?.header_text) : undefined;
      if (pageHeader) {
        return pageHeader;
      }

      if (experience) {
        return '';
      }

      let title = '';

      switch (currentRoute) {
        // The catalog is a variation of the pick step: a budget-only send lands there with no PYG
        // page of its own, so it greets the recipient the same way.
        case ROUTE_NAMES.marketplace:
        case ROUTE_NAMES.pyg: {
          title =
            pick_your_gift_title ||
            (isFirstStep
              ? `Hey${receiver_first_name ? ` ${receiver_first_name}, ` : ', '}pick your gift!`
              : `${receiver_first_name ? `${receiver_first_name}, pick` : 'Pick'} your gift!`);
          break;
        }
        case ROUTE_NAMES.pickMsku: {
          title =
            msku_page_title ||
            (isFirstStep
              ? `Hey${receiver_first_name ? ` ${receiver_first_name}` : ''}, select from the following options`
              : `${receiver_first_name ? `${receiver_first_name}, select` : 'Select'} from the following options`);
          break;
        }
        case ROUTE_NAMES.customizeItems: {
          title =
            customize_items_page_title ||
            (isFirstStep
              ? `Hey${receiver_first_name ? ` ${receiver_first_name}` : ''}, customize your items`
              : `${receiver_first_name ? `${receiver_first_name}, customize` : 'Customize'} your items`);
          break;
        }
        case ROUTE_NAMES.address: {
          title =
            shipping_page_title ||
            (isFirstStep
              ? `Hey${receiver_first_name ? ` ${receiver_first_name}` : ''}, the gift is almost ready!`
              : `${receiver_first_name ? `${receiver_first_name}, the` : 'The'} gift is almost ready!`);
          break;
        }
        default: {
          break;
        }
      }

      return title;
    },
  );

export const selectGreetingBody = (pathname: string) =>
  createSelector(selectExperience, selectPrioritizedWL, (experience, whiteLabeling) => {
    const { shipping_page_body, pick_your_gift_body, msku_page_body, customize_items_page_body } = whiteLabeling || {};

    const currentRoute = `/${pathname.split('/').at(-1)}`;

    const pageKey = getPageKey(currentRoute);
    const pageBody = pageKey ? nullifyEmpty(experience?.pages?.[pageKey]?.body_text) : undefined;
    if (pageBody) {
      return pageBody;
    }

    if (experience) {
      return '';
    }

    switch (currentRoute) {
      case ROUTE_NAMES.pyg:
        return pick_your_gift_body || 'Select one of the following packs you want to receive.';
      case ROUTE_NAMES.pickMsku:
        return msku_page_body || '';
      case ROUTE_NAMES.customizeItems:
        return customize_items_page_body || '';
      case ROUTE_NAMES.address:
        return shipping_page_body || '';
      default:
        return '';
    }
  });

export const selectBoxImage = (pathname: string) =>
  createSelector(
    selectExperience,
    selectWhiteLabeling,
    selectPickedCampaign,
    (experience, whiteLabeling, pickedCampaign) => {
      const currentRoute = `/${pathname.split('/').at(-1)}`;

      const boxImgUrl: string | undefined =
        getExperienceOverride(experience, currentRoute, (o) => o.header?.media?.image_url) ??
        nullifyEmpty(experience?.header?.media?.image_url);

      // When an experience is configured, it is authoritative for the box image:
      // show the configured media if set, otherwise render nothing (the user removed it).
      if (experience) {
        return boxImgUrl ?? null;
      }

      return nullifyEmpty(pickedCampaign?.ds_box_image_url) ?? whiteLabeling?.ds_box_image_url ?? null;
    },
  );

export const selectMappedPickedItems = createSelector(
  selectPickedItems,
  selectItems,
  selectMarketplaceCart,
  selectMarketplaceDescriptionFn,
  selectIsMarketplaceItemOutOfStockFn,
  (pickedItems, inventoryItems, cart, descriptionOf, isItemOutOfStock) => {
    // The single-source rule lets the cart win: a budget claim's selection lives there, while a
    // kit or campaign copied into inventory_items is what the recipient passed over.
    if (cart.length) {
      return mapCartLinesToCampaignItems(cart, descriptionOf);
    }

    // A marketplace item with every variant out of stock is dropped before anything is mapped, so
    // the shipping summary carries the same remaining items the options step let through. The
    // predicate is false for IE-catalog ids, which keep arriving untouched.
    const availableItems = inventoryItems?.filter((item) => !isItemOutOfStock(item.id)) ?? null;

    if (availableItems?.length) {
      const pickedByDefaultItems = getPickedItemsByDefault(availableItems);
      const pickedItemsArray = getUniqueItemsWithCounts(Object.values({ ...pickedItems, ...pickedByDefaultItems }));

      const skuOptionsArray = availableItems
        ?.map((item) => {
          if (item?.sku_options) {
            return item.sku_options;
          }

          return [item];
        })
        .flat();

      const resultItems: ICampaignItem[] = [];

      pickedItemsArray.forEach((item) => {
        if (item) {
          const { uid, quantity } = item;
          const itemToShow = skuOptionsArray.find((skuOption) => skuOption?.id === uid);
          if (itemToShow) {
            resultItems.push({
              ...itemToShow,
              quantity,
              name: getPickedMarketplaceItemName(itemToShow, availableItems),
            });
          }
        }
      });

      return resultItems;
    }

    return availableItems || [];
  },
);

export const selectCustomizableViewItems = createSelector(
  selectMappedPickedItems,
  selectCustomizableItems,
  (inventoryItems, customItems) =>
    Object.values(customItems || {}).map((customizableItem) => {
      const { internalId, item_id, item_customization_text } = customizableItem || {};
      const { name, image_url, customization } = inventoryItems?.find((i) => i.id === item_id) || {};

      const result: ICustomizableViewItem = {
        internalId,
        item_id,
        name,
        image_url,
        item_customization_text,
        ...customization,
      };

      return result;
    }),
);

const selectItemsWithRequiredCustomization = createSelector(selectCustomizableViewItems, (customizableItems) => {
  return customizableItems?.filter((item) => {
    return item?.item_customization_required;
  });
});

export const selectHasRequiredNonCustomizedItems = createSelector(
  selectItemsWithRequiredCustomization,
  (requiredCustomizationItems) => {
    return !!(
      requiredCustomizationItems.length &&
      requiredCustomizationItems.some((item) => {
        return !item?.item_customization_text;
      })
    );
  },
);

export const selectHasCustomizableItems = createSelector(
  selectCustomizableViewItems,
  (items) => items && items.length > 0,
);

export const selectHeroSectionBackground = (pathname: string) =>
  createSelector(selectExperience, selectBaseColor, (experience, baseColor) => {
    const currentRoute = `/${pathname.split('/').at(-1)}`;
    const overrideHero = getExperienceOverride(experience, currentRoute, (o) => o.header?.hero_section);
    const baseHero = experience?.header?.hero_section;

    const imageUrl = nullifyEmpty(overrideHero?.image_url) ?? nullifyEmpty(baseHero?.image_url);
    const backgroundColor = nullifyEmpty(overrideHero?.background_color) ?? nullifyEmpty(baseHero?.background_color);
    const imagePosition = nullifyEmpty(overrideHero?.image_position) ?? nullifyEmpty(baseHero?.image_position);
    const imageFit = nullifyEmpty(overrideHero?.image_fit) ?? nullifyEmpty(baseHero?.image_fit);

    if (imageUrl) {
      return {
        type: BackgroundTypeEnum.Image,
        value: imageUrl,
        position: imagePosition,
        fit: imageFit,
      };
    }

    if (backgroundColor) {
      return { type: BackgroundTypeEnum.Color, value: backgroundColor };
    }

    if (baseColor) {
      return { type: BackgroundTypeEnum.Color, value: baseColor };
    }

    return null;
  });

export const selectHeaderBackgroundColor = (pathname: string) =>
  createSelector(selectExperience, selectBaseColor, (experience, baseColor) => {
    const currentRoute = `/${pathname.split('/').at(-1)}`;

    return (
      getExperienceOverride(experience, currentRoute, (o) => o.header?.app_bar?.background_color) ??
      nullifyEmpty(experience?.header?.app_bar?.background_color) ??
      baseColor ??
      variables.defaultAccentColor
    );
  });

export const selectContentBackgroundColor = createSelector(
  selectExperience,
  (state: IAppState) => state.router.location.pathname,
  (experience, pathname) => {
    const currentRoute = `/${pathname.split('/').at(-1)}`;

    return (
      getExperienceOverride(experience, currentRoute, (o) => o.background?.color) ??
      nullifyEmpty(experience?.background?.color) ??
      null
    );
  },
);

export const selectFooterBackgroundColor = createSelector(
  selectExperience,
  (experience) => nullifyEmpty(experience?.background?.color) ?? null,
);

export const selectOrgLogoCandidates = (pathname: string) =>
  createSelector(
    selectExperience,
    selectOrgData,
    selectWhiteLabeling,
    (experience, orgInfo, whiteLabeling): string[] => {
      const currentRoute = `/${pathname.split('/').at(-1)}`;
      const experienceLogo =
        getExperienceOverride(experience, currentRoute, (o) => o.header?.app_bar?.logo_image_url) ??
        nullifyEmpty(experience?.header?.app_bar?.logo_image_url);

      return [experienceLogo, whiteLabeling?.ds_logo_url, orgInfo?.logo_url, DEFAULT_LOGO_URL].filter(
        (c): c is string => !!c,
      );
    },
  );

export const selectLogoScale = (pathname: string) =>
  createSelector(selectExperience, (experience): number | null => {
    const currentRoute = `/${pathname.split('/').at(-1)}`;
    const scale =
      getExperienceOverride(experience, currentRoute, (o) => o.header?.app_bar?.logo_image_size) ??
      experience?.header?.app_bar?.logo_image_size;

    if (typeof scale !== 'number' || !Number.isFinite(scale)) {
      return null;
    }

    return Math.min(Math.max(scale, LOGO_SCALE_MIN), LOGO_SCALE_MAX);
  });
